import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import type { User, PaginatedResponse } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, X, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/roles';

const ROLE_OPTIONS = [
    { value: 'dom', label: 'Dominant' },
    { value: 'sub', label: 'Submissive' },
    { value: 'switch', label: 'Switch' },
];

const SORT_OPTIONS = [
    { value: 'name', label: 'Name' },
    { value: 'rating', label: 'Top Rated' },
    { value: 'newest', label: 'Newest' },
];

export default function DiscoverMembersPage() {
    const [search, setSearch] = useState('');
    const [activeRoles, setActiveRoles] = useState<string[]>([]);
    const [sort, setSort] = useState('name');
    const [page, setPage] = useState(1);

    // Reset to page 1 when filters change
    useEffect(() => { setPage(1); }, [search, activeRoles, sort]);

    // Track searches with debounce
    useEffect(() => {
        if (!search) return;
        const timer = setTimeout(() => {
            trackEvent('discover_searched', { query: search });
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (activeRoles.length) params.set('role', activeRoles.join(','));
    if (sort !== 'name') params.set('sort', sort);
    if (page > 1) params.set('page', String(page));
    const qs = params.toString();

    const { data, isLoading } = useQuery({
        queryKey: ['discover-members', search, activeRoles, sort, page],
        queryFn: () => api.get<PaginatedResponse<User>>(`/discover/members${qs ? `?${qs}` : ''}`),
    });

    const members = data?.data ?? [];
    const meta = data?.meta;

    const toggleRole = (role: string) => {
        setActiveRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-semibold">Discover Members</h1>
                <p className="text-sm text-slate-400">Find and connect with active members</p>
            </div>

            {/* Search & Filters */}
            <div className="space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name or bio..."
                        className="pl-9"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {ROLE_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => toggleRole(opt.value)}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                activeRoles.includes(opt.value)
                                    ? 'bg-violet-600/30 text-violet-300 ring-1 ring-violet-500/50'
                                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                    <div className="ml-auto">
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 focus:border-violet-600 focus:outline-none"
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Results */}
            {isLoading ? (
                <p className="text-sm text-slate-400">Loading members...</p>
            ) : members.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-400">
                        {search || activeRoles.length ? 'No members match your search.' : 'No discoverable members yet.'}
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {members.map((member) => (
                            <Link key={member.id} to={`/profiles/${member.id}`}>
                                <Card className="h-full transition-shadow hover:shadow-lg hover:shadow-slate-900/50">
                                    <CardContent className="flex items-start gap-3 p-4">
                                        {member.avatar_path ? (
                                            <img
                                                src={member.avatar_path}
                                                alt=""
                                                className="h-12 w-12 shrink-0 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-900/50 text-lg font-bold text-violet-300">
                                                {member.display_name?.[0]?.toUpperCase() ?? '?'}
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="truncate font-medium text-sm">
                                                    {member.display_name}
                                                </span>
                                                <Badge className={`text-[10px] shrink-0 ${getRoleBadgeColor(member.role)}`}>
                                                    {getRoleLabel(member.role)}
                                                </Badge>
                                            </div>
                                            {member.bio && (
                                                <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">
                                                    {member.bio}
                                                </p>
                                            )}
                                            {(member.average_rating && Number(member.average_rating) > 0) && (
                                                <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                                                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                                    <span>{member.average_rating}</span>
                                                    {member.review_count ? (
                                                        <span>({member.review_count})</span>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>

                    {/* Pagination */}
                    {meta && meta.last_page > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="rounded-md border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800 disabled:opacity-30"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-xs text-slate-500">
                                Page {meta.current_page} of {meta.last_page}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                                disabled={page >= meta.last_page}
                                className="rounded-md border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800 disabled:opacity-30"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
